<template>
  <!-- render modal at top level to modal-destination -->
  <portal to="modal-destination" :order="order">
    <transition name="modal-slide" appear>
      <div class="aha-modal">
        <div
          class="aha-modal__backdrop"
          @click="onClickBackdrop()"
        />

        <div
          class="aha-modal__content"
          :class="`aha-modal__content--${size}`"
        >
          <div class="aha-modal__header" v-if="!hideHeader">
            <h4 class="aha-modal__header--text">
              <aha-svg
                size="lg"
                v-if="backable"
                name="arrow-tail"
                view-box="0 0 18 19"
                @click="handleBackButtonClick()"
                class="align-middle mr-3 cursor-pointer flex-shrink-0"
              />
              <span class="inline-block align-middle">{{ title }}</span>
            </h4>
            <span v-if="closable">
              <aha-icon
                name="close"
                height="20"
                width="20"
                viewBox="0 0 24 24"
                @click="onClickCloseButton()"
                class="cursor-pointer text-base-70 hover:text-primary-90"
              />
            </span>
          </div>
          <div
            class="aha-modal__body"
            :class="[{'pt-6': hideHeader}, `text-${textAlign}`, classes]"
          >
            <slot />
          </div>
          <template v-if="$slots.action">
            <div v-if="$slots.additional">
              <slot name="additional" />
            </div>
            <div :class="position" class="aha-modal__action">
              <slot name="action" />
            </div>
          </template>
        </div>
      </div>
    </transition>
  </portal>
</template>

<script>
import AhaIcon from './Icon.vue'
import AhaSvg from './Svg.vue'
import Escapable from '../mixins/Escapable.vue'

export default {
  components: {
    AhaIcon,
    AhaSvg,
  },
  mixins: [Escapable],
  props: {
    backable: {
      type: Boolean,
      default: false,
    },
    classes: {
      type: String,
      default: '',
    },
    closable: {
      type: Boolean,
      default: true,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'sm',
    },
    title: {
      type: String,
      default: null,
    },
    textAlign: {
      type: String,
      default: 'left',
    },
    position: {
      type: String,
      default: 'justify-end',
    },
    priority: {
      type: String,
      default: 'medium',
    },
    noCloseOnBackdrop: {
      type: Boolean,
      default: false,
    },
    noCloseOnEsc: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modalVisible: false,
    }
  },
  computed: {
    order() {
      if (this.priority === 'medium') return 2

      if (this.priority === 'high') return 3

      return 1
    },
    escapableVisible() {
      return this.modalVisible
    },
  },
  created() {
    this.CLOSE_METHODS = {
      ESC_KEY: 'esc key',
      CLOSE_BUTTON: 'close button',
      BACKDROP_CLICK: 'backdrop click',
    }
  },
  mounted() {
    this.modalVisible = true
  },
  methods: {
    handleBackButtonClick() {
      this.$emit('back')
    },
    onClickBackdrop() {
      if (!this.noCloseOnBackdrop) {
        this.$emit('hide', this.CLOSE_METHODS.BACKDROP_CLICK)
        this.gaTrackEvent('Modal', `Close modal by ${this.CLOSE_METHODS.BACKDROP_CLICK}`, this.title)
      }
    },
    onClickCloseButton() {
      this.$emit('hide', this.CLOSE_METHODS.CLOSE_BUTTON)
      this.gaTrackEvent('Modal', `Close modal by ${this.CLOSE_METHODS.CLOSE_BUTTON}`, this.title)
    },
    onEsc() {
      if (!this.noCloseOnEsc) {
        this.$emit('hide', this.CLOSE_METHODS.ESC_KEY)
        this.gaTrackEvent('Modal', `Close modal by ${this.CLOSE_METHODS.ESC_KEY}`, this.title)
      }
    },
    gaTrackEvent(eventCategory = '', eventAction = '', eventLabel = '') {
      if (window.ga) {
        window.ga('send', 'event', eventCategory, eventAction, eventLabel)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.modal-slide-enter-active,
.modal-slide-leave-active {
  @apply transition-opacity duration-300;

  .aha-modal__content {
    @apply transition-transform duration-300;
  }
}

.modal-slide-enter,
.modal-slide-leave-to {
  @apply opacity-0;

  .aha-modal__content {
    transform: translateY(-200px);
  }
}

.aha-modal {
  @apply fixed z-10 inset-0 flex items-center justify-center;
}

.aha-modal__body {
  @apply px-6 mb-5 overflow-auto text-sm break-words;
}

.aha-modal__content {
  @apply relative rounded-lg bg-base-0 max-h-screen overflow-auto;
}

.aha-modal__content--sm {
  @apply w-108;
}

.aha-modal__content--3xl {
  width: 48rem;
}

.aha-modal__action {
  @apply flex items-center px-6 pb-6;
}

.aha-modal__backdrop {
  @apply absolute inset-0 bg-base-100 opacity-30;
}

.aha-modal__header {
  @apply flex justify-between py-4 pl-6 pr-3;
}

.aha-modal__header .aha-modal__header--text {
  @apply mt-2 text-base font-bold text-base-100 break-words flex;
  width: calc(100% - 20px);
}
</style>
